import React, { useMemo, useState, useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { projects } from "../data/projects";
import { useLanguage } from "../context/LanguageContext";

// Map project categories to user-facing category groupings
const categoryImages = {
  villas: "https://customer-assets.emergentagent.com/job_precision-build-25/artifacts/p6j88y5t_ChatGPT%20Image%20Mar%203%2C%202026%2C%2008_57_12%20PM.png",
  energy: "https://customer-assets.emergentagent.com/job_precision-build-25/artifacts/nbkkojbz_080.png",
  commercial: "https://customer-assets.emergentagent.com/job_precision-build-25/artifacts/8jqkij7h_IMG_2897.jpeg",
  mechanical: "https://customer-assets.emergentagent.com/job_precision-build-25/artifacts/29da9j9s_IMG_2388.jpeg",
};

const Projects = () => {
  const { t, lang } = useLanguage();
  const [openProject, setOpenProject] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);

  const visible = useMemo(() => projects, []);

  const open = (p) => { setOpenProject(p); setImgIdx(0); };
  const close = useCallback(() => setOpenProject(null), []);
  const prev = useCallback(() => setImgIdx((i) => (openProject ? (i - 1 + openProject.images.length) % openProject.images.length : 0)), [openProject]);
  const next = useCallback(() => setImgIdx((i) => (openProject ? (i + 1) % openProject.images.length : 0)), [openProject]);

  useEffect(() => {
    if (!openProject) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [openProject, close, prev, next]);

  return (
    <section id="projects" data-testid="projects-section" className="relative bg-[#F7F5F2] py-32 lg:py-44">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-20">
          <div>
            <div className="flex items-center gap-4 mb-8">
              <span className="w-14 h-px bg-[#B08D57]"></span>
              <span className="overline-bold text-[#B08D57]">{t.projects.overline}</span>
            </div>
            <h2 className="font-display-thin text-[#0F0F0F] text-4xl sm:text-5xl lg:text-[72px] tracking-tight leading-none">
              {t.projects.title}
            </h2>
            <p className="text-[#3A3A37] mt-6 max-w-xl text-base leading-[1.75]">{t.projects.subtitle}</p>
          </div>
        </div>

        {/* Category cards (4 luxury blocks) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-24">
          {t.projects.categories.map((c, i) => (
            <div
              key={c.id}
              data-testid={`project-category-${c.id}`}
              className="group relative overflow-hidden bg-white hover-lift cursor-pointer"
              onClick={() => {
                if (visible[0]) open(visible[0]);
              }}
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img src={categoryImages[c.id]} alt={c.title} className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1500ms]" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent"></div>
                <div className="absolute top-5 left-5 overline text-[#B08D57]">{String(i + 1).padStart(2, "0")}</div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-display text-white text-xl lg:text-[22px] leading-tight mb-2 group-hover:text-[#B08D57] transition-colors">{c.title}</h3>
                  <p className="text-white/75 text-[13px] leading-[1.6] mb-5">{c.desc}</p>
                  <div className="inline-flex items-center gap-2 overline text-[#B08D57]">
                    {t.projects.view}
                    <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Actual project gallery */}
        <div className="border-t border-[#0F0F0F]/10 pt-20">
          <div className="overline-bold text-[#B08D57] mb-10">— Recent Work</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {visible.map((p) => (
              <button
                key={p.id}
                data-testid={`project-card-${p.id}`}
                onClick={() => open(p)}
                className="group text-left"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-[#ECE8E1]">
                  <img
                    src={p.cover}
                    alt={lang === "el" ? p.title_el : p.title_en}
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1500ms]"
                    loading="lazy"
                  />
                  {p.in_progress && (
                    <>
                      {/* Premium "Under Construction" presentation seal */}
                      <div className="absolute top-5 left-5 right-5 flex items-start justify-between">
                        <div className="bg-[#0F0F0F] text-white px-4 py-2.5 border-l-2 border-[#B08D57]">
                          <div className="flex items-center gap-2.5">
                            <span className="w-1.5 h-1.5 bg-[#B08D57] rounded-full animate-pulse"></span>
                            <span className="overline-bold text-[#B08D57] text-[10px]">{t.projects.in_progress}</span>
                          </div>
                          <div className="text-white text-[10px] tracking-[0.2em] uppercase mt-1 font-light">Project · 2025</div>
                        </div>
                      </div>
                      {/* Bottom-right framed badge */}
                      <div className="absolute bottom-5 right-5 border border-[#B08D57] bg-black/30 backdrop-blur px-3 py-2 text-center">
                        <div className="font-display-thin text-[#B08D57] text-2xl leading-none">N°{String(p.images.length).padStart(2, "0")}</div>
                        <div className="overline text-white/80 text-[8px] mt-1">Presentation</div>
                      </div>
                    </>
                  )}
                  {!p.in_progress && p.images.length > 1 && (
                    <span className="absolute bottom-5 right-5 overline text-white/85">
                      {String(p.images.length).padStart(2, "0")}
                    </span>
                  )}
                </div>
                <div className="mt-6">
                  <div className="overline text-[#B08D57] mb-3">{t.projects.filters[p.category]}</div>
                  <h3 className="font-display text-[#0F0F0F] text-xl leading-tight group-hover:text-[#B08D57] transition-colors">
                    {lang === "el" ? p.title_el : p.title_en}
                  </h3>
                  <div className="mt-3 flex items-center gap-3 text-[11px] tracking-[0.15em] uppercase text-[#5C5C58]">
                    <span>{lang === "el" ? p.location_el : p.location_en}</span>
                    <span className="w-1 h-1 bg-[#B08D57] rounded-full"></span>
                    <span>{p.year}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {openProject && (
        <div
          data-testid="project-lightbox"
          className="fixed inset-0 z-[100] bg-black/97 backdrop-blur-xl flex flex-col"
          onClick={close}
        >
          <div className="flex items-center justify-between p-6 md:p-10" onClick={(e) => e.stopPropagation()}>
            <div>
              <div className="overline text-[#B08D57]">{t.projects.filters[openProject.category]}</div>
              <h3 className="font-display-thin text-white text-2xl sm:text-3xl mt-2">
                {lang === "el" ? openProject.title_el : openProject.title_en}
              </h3>
              <div className="mt-2 text-[10px] tracking-[0.2em] uppercase text-white/50">
                {lang === "el" ? openProject.location_el : openProject.location_en} · {openProject.year}
              </div>
            </div>
            <button
              data-testid="lightbox-close"
              onClick={close}
              className="text-white/70 hover:text-[#B08D57] p-2 transition-colors"
              aria-label={t.projects.close}
            >
              <X className="w-6 h-6" strokeWidth={1} />
            </button>
          </div>

          <div className="flex-1 flex items-center justify-center px-4 py-4 relative" onClick={(e) => e.stopPropagation()}>
            <button
              data-testid="lightbox-prev"
              onClick={prev}
              className="absolute left-3 md:left-8 z-10 w-10 h-10 flex items-center justify-center hover:text-[#B08D57] text-white/70 transition-colors"
              aria-label="Previous"
            >
              <ChevronLeft className="w-7 h-7" strokeWidth={1} />
            </button>
            <img
              src={openProject.images[imgIdx]}
              alt=""
              className="max-h-[72vh] max-w-[88vw] object-contain"
            />
            <button
              data-testid="lightbox-next"
              onClick={next}
              className="absolute right-3 md:right-8 z-10 w-10 h-10 flex items-center justify-center hover:text-[#B08D57] text-white/70 transition-colors"
              aria-label="Next"
            >
              <ChevronRight className="w-7 h-7" strokeWidth={1} />
            </button>
          </div>

          <div className="px-6 md:px-10 pb-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-center gap-2 overflow-x-auto pb-2">
              {openProject.images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setImgIdx(i)}
                  className={`shrink-0 w-16 h-16 sm:w-20 sm:h-20 overflow-hidden ${
                    i === imgIdx ? "opacity-100 ring-1 ring-[#B08D57]" : "opacity-40 hover:opacity-70"
                  } transition-all`}
                  data-testid={`lightbox-thumb-${i}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            <div className="mt-4 text-center overline text-white/40">
              {String(imgIdx + 1).padStart(2, "0")} / {String(openProject.images.length).padStart(2, "0")}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Projects;
